import React, { useCallback, useMemo, useReducer } from 'react';
import { NotificationType } from './notificationModel';
import {
    addNotificationToStack,
    notificationsReducer,
    removeNotification as removeNotificationAction,
} from './notificationsReducer';
import { NotificationsContextProvider } from './notificationsContext';

// Running id counter — mirrors the legacy `nextNotificationId` used by the
// `addNotification` thunk.
let nextNotificationId = 0;

/**
 * Provider for the app-wide toast notification stack.
 *
 * Owns the stack state (via `useReducer`) and exposes it through
 * `notificationsContext`; the host `<NotificationsStack />` reads the stack
 * from the same context, so the provider must wrap wherever the stack is
 * rendered (mounted once in `App.js`).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export const NotificationsProvider = ({ children }) => {
    const [stack, dispatch] = useReducer(notificationsReducer, []);

    const pushNotif = useCallback((payload) => {
        const {
            message,
            description,
            type = NotificationType.Success,
            canExpire = true,
            onClose,
            action,
        } = payload;

        dispatch(
            addNotificationToStack({
                id: nextNotificationId++,
                type,
                message,
                description,
                onClose,
                action,
                canExpire,
            })
        );
    }, []);

    const removeNotification = useCallback((notificationId) => {
        dispatch(removeNotificationAction(notificationId));
    }, []);

    const value = useMemo(
        () => ({ pushNotif, stack, removeNotification }),
        [pushNotif, stack, removeNotification]
    );

    return (
        <NotificationsContextProvider value={value}>
            {children}
        </NotificationsContextProvider>
    );
};

export default NotificationsProvider;